import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg, imgUrl } from "@/lib/api";
import { RESOURCES } from "@/admin/config";
import ItemForm from "@/admin/ItemForm";

const CREATE_IDS = { galleries: "admin-create-gallery-button", articles: "admin-create-article-button" };

export default function ResourceManager({ name }) {
  const config = RESOURCES[name];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/${name}`).then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
  }, [name]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/${name}/${data.id}`, data);
      else await api.post(`/admin/${name}`, data);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    await api.delete(`/admin/${name}/${item.id}`).catch((e) => toast.error(errMsg(e)));
    load();
  };

  const live = (r) => (name === "notices" ? r.active : r.published);

  return (
    <div data-testid={`admin-${name}-panel`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl sm:text-3xl text-[#1a261f]">{config.label}</h2>
        <button onClick={() => setEditing({})} data-testid={CREATE_IDS[name] || `admin-create-${name}-button`}
          className="inline-flex items-center gap-2 bg-[#1f402b] text-white px-4 py-2.5 text-sm hover:bg-[#2c5a3d]">
          <Plus size={15} /> New {config.singular}
        </button>
      </div>
      <div className="border border-[#e2dcd4] bg-white divide-y divide-[#e2dcd4]">
        {items.length === 0 && <p className="p-6 text-sm text-[#5a665e]">Nothing here yet.</p>}
        {items.map((r) => (
          <div key={r.id} className="flex items-center gap-4 p-4" data-testid={`admin-row-${r.id}`}>
            {(r.cover_image) && <img src={imgUrl(r.cover_image)} alt="" className="w-16 h-12 object-cover hidden sm:block" />}
            <div className="flex-1 min-w-0">
              <p className="truncate text-[#1a261f]">{r.title}</p>
              <p className="font-mono-k text-[11px] text-[#5a665e] truncate">{config.summary(r)}</p>
            </div>
            <span className={`font-mono-k text-[10px] uppercase tracking-widest px-2 py-1 ${live(r) ? "bg-[#eef3ee] text-[#1f402b]" : "bg-[#f4f0ea] text-[#5a665e]"}`}>
              {live(r) ? "Live" : "Draft"}
            </span>
            <button onClick={() => setEditing(r)} className="p-2 text-[#5a665e] hover:text-[#1f402b]" data-testid={`admin-edit-${r.id}`} aria-label="Edit"><Pencil size={15} /></button>
            <button onClick={() => remove(r)} className="p-2 text-[#5a665e] hover:text-[#991b1b]" data-testid={`admin-delete-${r.id}`} aria-label="Delete"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      {editing && <ItemForm config={config} item={editing.id ? editing : null} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
